import { useCallback, useEffect, useRef, useState } from 'react';
import { Microphone, Pause, Play } from '@phosphor-icons/react';
import type { FeaturedProject, VideoRef } from '../../content/site';
import { fr } from '../../content/typography';
import { TiltCard } from './TiltCard';
import { gsap, useGSAP, EASE, MOTION_OK, REDUCED } from '../../lib/gsap';
import { useReducedMotion } from '../../lib/useReducedMotion';

type VideoLabels = { play: string; pause: string };

/**
 * Part of a recording that is clean, by file, in seconds. The Vox demo ends on the macOS
 * capture toolbar (from about 33.1 s) and shows the pointer for an instant at 0.3 s: the
 * loop stays inside the clean span. Remove the entry once the file is re-exported trimmed.
 */
const CLEAN_SPAN: Record<string, { start: number; end: number }> = {
	'/img/work/vox-demo.mp4': { start: 0.7, end: 32.7 },
};

/** Wide strips (the pill bar, about 7:1) against the demo itself. */
const isStrip = (video: VideoRef) => video.width / video.height > 3;

type Playback = {
	register: (video: HTMLVideoElement | null) => void;
	playing: boolean;
	toggle: () => void;
};

/**
 * Plays the article's recordings only while they are in view (IntersectionObserver, no
 * scroll listener) and pauses them outside. With motion allowed they start on their own;
 * under reduced motion nothing starts until the reader asks. One control plays or pauses
 * them all, and a pause chosen by the reader is kept when the video comes back into view.
 */
function usePlayback(reduced: boolean): Playback {
	const videos = useRef(new Set<HTMLVideoElement>());
	const visible = useRef(new Set<HTMLVideoElement>());
	const intent = useRef<'auto' | 'play' | 'pause'>('auto');
	const [playing, setPlaying] = useState(false);
	const [observer, setObserver] = useState<IntersectionObserver | null>(null);

	const sync = useCallback(() => {
		const wanted = intent.current === 'play' || (intent.current === 'auto' && !reduced);
		videos.current.forEach((video) => {
			if (wanted && visible.current.has(video)) {
				video.muted = true;
				const attempt = video.play();
				// Autoplay can be refused (power saving): the poster stays, the control still works.
				if (attempt) attempt.catch(() => setPlaying(false));
			} else if (!video.paused) {
				video.pause();
			}
		});
	}, [reduced]);

	useEffect(() => {
		if (typeof IntersectionObserver === 'undefined') return undefined;
		const io = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const video = entry.target as HTMLVideoElement;
					if (entry.isIntersecting) visible.current.add(video);
					else visible.current.delete(video);
				});
				sync();
			},
			// Any visible part counts: the control under the frame always acts on a video in view.
			{ threshold: 0 },
		);
		videos.current.forEach((video) => io.observe(video));
		setObserver(io);
		return () => {
			io.disconnect();
			setObserver(null);
		};
	}, [sync]);

	// The motion preference changed: start or stop accordingly.
	useEffect(() => {
		sync();
	}, [sync]);

	const register = useCallback(
		(video: HTMLVideoElement | null) => {
			if (!video || videos.current.has(video)) return;
			videos.current.add(video);
			observer?.observe(video);
			const span = CLEAN_SPAN[video.getAttribute('data-src') ?? ''];
			const update = () => setPlaying(Array.from(videos.current).some((v) => !v.paused));
			video.addEventListener('play', update);
			video.addEventListener('pause', update);
			if (span) {
				const keepInSpan = () => {
					if (video.currentTime < span.start - 0.05 || video.currentTime >= span.end) video.currentTime = span.start;
				};
				video.addEventListener('loadedmetadata', keepInSpan);
				video.addEventListener('timeupdate', keepInSpan);
			}
		},
		[observer],
	);

	const toggle = () => {
		intent.current = playing ? 'pause' : 'play';
		sync();
	};

	return { register, playing, toggle };
}

/** A recording, shown like the screenshots; never anything drawn over it. */
function Recording({
	video,
	register,
	className = '',
}: {
	video: VideoRef;
	register: (video: HTMLVideoElement | null) => void;
	className?: string;
}) {
	return (
		<video
			ref={register}
			data-src={video.src}
			muted
			loop
			playsInline
			preload="none"
			poster={video.poster}
			width={video.width}
			height={video.height}
			aria-label={video.label}
			className={className}
		>
			<source src={video.src} type="video/mp4" />
		</video>
	);
}

/**
 * Vox, a voice pill that drives AI coding agents. The article is told in the order of a
 * real use: the command as spoken (display type, a microphone badge, French quotes, so it
 * reads as an utterance and not as a testimonial), the pill bar that hears it, then the
 * demo, warm and cinematic, alone, at full width. A line under the frame says
 * what the demo shows and holds the one play/pause control, outside the picture.
 *
 * The pill bar stays legible: from 30rem it is shown whole (its labels land around
 * 13 to 17px), below it is cropped at a fixed, readable height onto the words it
 * transcribes. A thin purple edge baked into the right of the recording is cropped away.
 * Motion: the command appears word by word, as Vox transcribes. Reduced motion: static
 * command, recordings on their posters until played.
 */
export function VoiceDemo({ project, labels }: { project: FeaturedProject; labels: VideoLabels }) {
	const root = useRef<HTMLDivElement>(null);
	const reduced = useReducedMotion();
	const { register, playing, toggle } = usePlayback(reduced);
	const videos = project.videos ?? [];
	const strip = videos.find(isStrip);
	const demo = videos.find((video) => video !== strip);
	const words = project.command ? fr(`« ${project.command} »`).split(' ') : [];

	useGSAP(
		() => {
			const el = root.current;
			if (!el) return;
			const mm = gsap.matchMedia();
			mm.add({ motion: MOTION_OK, reduced: REDUCED }, (context) => {
				// Reduced motion: the command is simply there.
				if (context.conditions?.reduced) return;
				const command = el.querySelector('[data-voice-command]');
				if (!command) return;
				const tl = gsap.timeline({ scrollTrigger: { trigger: command, start: 'top 80%', once: true } });
				tl.from(command.querySelector('[data-voice-badge]'), { scale: 0.5, autoAlpha: 0, duration: 0.8, ease: EASE }, 0);
				tl.from(command.querySelectorAll('[data-voice-word]'), {
					opacity: 0.12,
					duration: 0.5,
					ease: 'power1.out',
					stagger: 0.085,
				}, 0.25);
			});
		},
		{ scope: root },
	);

	const Icon = playing ? Pause : Play;

	return (
		<div ref={root}>
			{words.length > 0 && (
				<p
					data-voice-command
					className="flex items-start gap-[0.45em] font-display text-[length:clamp(1.625rem,3.4vw,2.75rem)] font-medium leading-[1.12] tracking-[-0.025em] text-graphite dark:text-mineral"
				>
					<span
						data-voice-badge
						aria-hidden
						className="mt-[0.12em] inline-flex size-[1.1em] shrink-0 items-center justify-center rounded-full bg-forest text-mineral dark:bg-solar dark:text-graphite"
					>
						<Microphone weight="fill" className="size-[0.55em]" />
					</span>
					<span className="text-balance">
						{words.map((word, i) => (
							<span key={i} data-voice-word>
								{word}
								{i < words.length - 1 ? ' ' : ''}
							</span>
						))}
					</span>
				</p>
			)}

			{strip && (
				<div className="relative mt-[clamp(1.5rem,4cqi,2.5rem)] h-[4.75rem] max-w-[45rem] overflow-hidden rounded-field bg-graphite shadow-[0_18px_40px_-24px_rgba(41,77,59,0.5)] dark:ring-1 dark:ring-mineral/10 [@container(min-width:30rem)]:aspect-[1412/210] [@container(min-width:30rem)]:h-auto">
					<Recording
						video={strip}
						register={register}
						className="absolute inset-0 h-full w-full object-cover object-[6%_50%] [@container(min-width:30rem)]:object-left"
					/>
				</div>
			)}

			{demo && (
				<figure className="mt-[clamp(0.75rem,1.6vw,1.25rem)] max-w-[50rem]">
					<TiltCard>
						<Recording video={demo} register={register} className="block h-auto w-full" />
					</TiltCard>
					<figcaption className="mt-3 flex items-center justify-between gap-6">
						<span aria-hidden className="max-w-[52ch] text-[0.9375rem] leading-snug text-graphite-muted dark:text-dusk-muted">
							{demo.label}
						</span>
						<button
							type="button"
							onClick={toggle}
							aria-label={playing ? labels.pause : labels.play}
							className="inline-flex size-11 shrink-0 items-center justify-center rounded-full border border-graphite/20 text-graphite dark:border-mineral/25 dark:text-mineral transition-[background-color,border-color,transform] duration-300 ease-expo hover:border-graphite/50 hover:bg-graphite/[0.04] dark:hover:border-mineral/60 dark:hover:bg-mineral/[0.06] active:scale-[0.96]"
						>
							<Icon aria-hidden weight="fill" size={16} />
						</button>
					</figcaption>
				</figure>
			)}
		</div>
	);
}
